/**
 * RetentionPanel — shared inactive-customers render tree (EX-C Step 5).
 *
 * Consumed by:
 *   • frontend/src/pages/owner/RetentionPage.jsx
 *     (data via /api/owner/inactive-customers)
 *   • frontend/src/pages/admin/BrandRetentionPage.jsx       [EX-C Step 6]
 *     (data via /api/admin/brands/{id}/inactive-customers)
 *
 * Pure presentational component — no data fetching, no React Query hooks.
 * The parent passes the already-resolved response plus the loading flags
 * and the threshold / refresh / row-click handlers. `showEmail` toggles
 * the email column visibility (owner=true, admin=false).
 *
 * testIDs (operator contract — must not break across EX-C steps):
 *   retention-panel                      (root)
 *   retention-threshold-30 / 60 / 90     (tab buttons)
 *   retention-last-updated
 *   retention-refresh-button
 *   retention-summary-chart
 *   retention-items-table
 *   retention-row-{customer_id}          (each row)
 *   retention-empty-state
 *   retention-pagination-prev
 *   retention-pagination-next
 *   retention-pagination-info
 */
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { RefreshCw, UserX, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import { SectionCard, EmptyState } from '@/components/ui_primitives/Misc';

const THRESHOLD_OPTIONS = [30, 60, 90];

const BUCKET_BARS = [
  { key: 'inactive_30d',       label: '30-59 days',  color: '#C9A84C' },
  { key: 'inactive_60d',       label: '60-89 days',  color: '#F0B040' },
  { key: 'inactive_90d',       label: '90-179 days', color: '#E07A3F' },
  { key: 'inactive_180d_plus', label: '180d+',       color: '#C84A3F' },
];

function formatClockHMS(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour12: false });
  } catch (_) {
    return '—';
  }
}

function daysInactiveBadgeStyle(days) {
  // Locked semantics: 30-59=gold, 60-89=orange, 90+=red. Used both for
  // the table cell and conceptually mirrored in the bar chart colours.
  if (days == null) return 'border-white/15 bg-white/5 text-white/60';
  if (days < 60)  return 'border-gold/30 bg-gold/10 text-gold';
  if (days < 90)  return 'border-orange-400/30 bg-orange-400/10 text-orange-300';
  return 'border-rose-400/30 bg-rose-400/10 text-rose-300';
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0E0E13] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <div className="text-white/50">{label}</div>
      <div className="text-white font-semibold mt-0.5">
        {payload[0].value} customer{payload[0].value === 1 ? '' : 's'}
      </div>
    </div>
  );
};

export default function RetentionPanel({
  data,
  thresholdDays = 30,
  onThresholdChange,
  onRefresh,
  onCustomerClick,
  isLoading = false,
  isFetching = false,
  showEmail = true,
}) {
  const d = data || {};
  const summary = d.summary || {};
  const items = (d.page?.items) || [];
  const limit = d.page?.limit ?? 50;
  const offset = d.page?.offset ?? 0;
  const total = d.total_inactive_count ?? 0;

  // Bar chart input — all 4 buckets, always emitted so the x-axis stays
  // stable across thresholds even when individual values are zero.
  const chartData = BUCKET_BARS.map((b) => ({
    label: b.label,
    count: Number(summary[b.key] ?? 0),
    fill: b.color,
  }));

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + items.length, total);
  const hasPrev = offset > 0;
  const hasNext = offset + items.length < total;

  return (
    <div className="space-y-8" data-testid="retention-panel">
      {/* Threshold tabs + last-updated + refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
          {THRESHOLD_OPTIONS.map((days) => {
            const active = days === thresholdDays;
            return (
              <button
                key={days}
                type="button"
                onClick={() => onThresholdChange && onThresholdChange(days)}
                data-testid={`retention-threshold-${days}`}
                className={
                  'px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ' +
                  (active
                    ? 'bg-gold text-black shadow-sm'
                    : 'text-white/60 hover:text-white hover:bg-white/5')
                }
              >
                {days} days
              </button>
            );
          })}
        </div>
        <div
          className="flex items-center gap-2 text-xs text-white/55 font-mono"
          data-testid="retention-last-updated"
        >
          <span>Updated {formatClockHMS(d.as_of)}</span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isFetching || isLoading}
            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 hover:bg-gold/10 hover:border-gold/30 text-white/70 hover:text-gold grid place-items-center transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="retention-refresh-button"
            aria-label="Refresh retention data"
            title="Refresh"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Summary distribution bar chart */}
      <SectionCard testID="retention-summary-card">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-white">
            Inactivity distribution
          </h3>
          <span className="text-xs font-mono text-white/40">
            {Number(total).toLocaleString('id-ID')} inactive at {thresholdDays}d threshold
          </span>
        </div>
        <div className="h-52" data-testid="retention-summary-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ left: -20, right: 12, top: 8, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(212,175,55,0.06)' }}
                content={<ChartTooltip />}
              />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {chartData.map((b, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Bar key={i} dataKey="count" fill={b.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      {/* Items table */}
      <SectionCard testID="retention-items-card" padded={false}>
        <div className="px-6 pt-5 pb-3 flex items-baseline justify-between">
          <h3 className="font-display text-lg font-semibold text-white">
            Inactive customers
          </h3>
          {total > 0 ? (
            <span
              className="text-xs font-mono text-white/40"
              data-testid="retention-pagination-info"
            >
              Showing {pageStart}-{pageEnd} of {total}
            </span>
          ) : null}
        </div>
        {total === 0 ? (
          <div className="px-6 pb-8">
            <EmptyState
              icon={<UserX size={28} />}
              title="No inactive customers at this threshold"
              subtitle="Your customers are engaged — nice work."
              testID="retention-empty-state"
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto" data-testid="retention-items-table">
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-white/45 border-b border-white/8">
                  <tr>
                    <th className="px-6 py-2 font-medium">Name</th>
                    <th className="px-6 py-2 font-medium">Membership</th>
                    {showEmail ? <th className="px-6 py-2 font-medium">Email</th> : null}
                    <th className="px-6 py-2 font-medium text-right">Stamps</th>
                    <th className="px-6 py-2 font-medium text-right">Total earned</th>
                    <th className="px-6 py-2 font-medium">Days inactive</th>
                    <th className="px-6 py-2 font-medium">Last transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const daysInactive =
                      item.days_since_last_transaction ?? item.days_since_joined;
                    const badgeStyle = daysInactiveBadgeStyle(daysInactive);
                    const clickable = !!onCustomerClick;
                    return (
                      <tr
                        key={item.customer_id}
                        data-testid={`retention-row-${item.customer_id}`}
                        onClick={
                          clickable ? () => onCustomerClick(item) : undefined
                        }
                        className={
                          'border-b border-white/5 ' +
                          (clickable
                            ? 'hover:bg-white/[0.025] cursor-pointer transition-colors'
                            : '')
                        }
                      >
                        <td className="px-6 py-3 font-medium text-white">
                          {item.name}
                        </td>
                        <td className="px-6 py-3 font-mono text-xs text-white/55">
                          {item.masked_membership_id || '—'}
                        </td>
                        {showEmail ? (
                          <td className="px-6 py-3 text-xs text-white/55">
                            {item.email || '—'}
                          </td>
                        ) : null}
                        <td className="px-6 py-3 text-right tabular-nums text-white/80">
                          {item.current_stamps ?? 0}
                        </td>
                        <td className="px-6 py-3 text-right tabular-nums text-white/55">
                          {item.total_stamps_earned ?? 0}
                        </td>
                        <td className="px-6 py-3">
                          <span
                            className={
                              'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono border ' +
                              badgeStyle
                            }
                          >
                            {daysInactive == null ? '—' : `${daysInactive}d`}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-xs">
                          {item.last_transaction_at ? (
                            <span className="text-white/70">
                              {formatDateTime(item.last_transaction_at)}
                            </span>
                          ) : (
                            <div>
                              <div className="text-white/45 italic">Never</div>
                              <div className="text-white/30 text-[10px]">
                                Joined {item.days_since_joined ?? 0} days ago
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination footer */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-white/8">
              <span className="text-xs font-mono text-white/40">
                {limit} per page
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onThresholdChange && onThresholdChange(thresholdDays, Math.max(0, offset - limit))}
                  disabled={!hasPrev}
                  data-testid="retention-pagination-prev"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={12} /> Prev
                </button>
                <button
                  type="button"
                  onClick={() => onThresholdChange && onThresholdChange(thresholdDays, offset + limit)}
                  disabled={!hasNext}
                  data-testid="retention-pagination-next"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next <ChevronRight size={12} />
                </button>
              </div>
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}
